import "./tournament-message-recipient-manage.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  defaultPadding,
  GSActionBar,
  GSEmptyList,
  GSInfiniteList,
  GSInput,
  GSItemList,
  GSRadioGroup,
  GSSidePanelPage,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faPlus,
  faSave,
  faSearch,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getMessage,
  refreshMessageRecipients,
  getRecipientGroup,
  updateRecipientGroup,
  updateRecipient,
  selectCurrentRecipientGroup,
  selectMessagesLoading,
  selectMessagesNotifications,
  clearNotifications,
  clearCurrentRecipientGroup,
  selectCurrentMessage,
} from "../../../reducers/messagesSlice";
import { useNotificationBanner } from "../../../hooks/notificationHooks";
import { getNotificationItemInfo } from "../../../helpers/Converters";
import {
  RECIPIENT_GROUP_LABELS,
  RecipientListItem,
  RecipientModeBanner,
} from "../../../forms/MessageForms";

const DEFAULT_FILTER = { page: { number: 1, size: 25 }, q: "" };

const TournamentMessageRecipientManage = ({ viewOnly = false }) => {
  const navigate = useNavigate();
  const { id: tournamentID, messageID, groupID } = useParams();
  const dispatch = useDispatch();
  const recipientGroup = useSelector(selectCurrentRecipientGroup);
  const currentMessage = useSelector(selectCurrentMessage);
  const loading = useSelector(selectMessagesLoading);
  const notifications = useSelector(selectMessagesNotifications);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const silentRefetch = useRef(false);

  useEffect(() => {
    if (!tournamentID || !messageID || !groupID) return;
    if (viewOnly) {
      dispatch(getMessage({ tournamentID, messageID }));
    } else {
      dispatch(
        refreshMessageRecipients({ tournamentID, messageID, recipientGroupId: groupID }),
      ).then(() => {
        silentRefetch.current = true;
        setFilter((prev) => ({ ...prev, page: { ...prev.page, number: 1 } }));
      });
    }
    return () => dispatch(clearCurrentRecipientGroup());
  }, [dispatch, tournamentID, messageID, groupID, viewOnly]);

  const submitSearch = () => {
    setFilter((prev) => ({ ...prev, q: search, page: { ...prev.page, number: 1 } }));
  };

  const clearSearch = () => {
    setSearch("");
    setFilter((prev) => ({ ...prev, q: "", page: { ...prev.page, number: 1 } }));
  };

  const messageGroup = currentMessage?.recipientGroups?.find(
    (g) => g.id === groupID
  );
  const includeAll = recipientGroup?.includeAll ?? messageGroup?.includeAll;
  const isIncludeAll = includeAll === true;
  const selectedFilter = viewOnly && !isIncludeAll ? true : undefined;
  const waitForMessage = viewOnly && includeAll === undefined;

  useEffect(() => {
    if (!groupID || waitForMessage) return;
    dispatch(
      getRecipientGroup({
        groupID,
        page: filter.page.number,
        perPage: filter.page.size,
        q: filter.q || undefined,
        selected: selectedFilter,
      })
    ).then(() => {
      silentRefetch.current = false;
    });
  }, [dispatch, groupID, filter, selectedFilter, waitForMessage]);

  const groupLabel = RECIPIENT_GROUP_LABELS[recipientGroup?.groupType] ?? "Recipients";
  const waitlistLabel =
    recipientGroup?.groupType === "waitlist" ? "Waitlist Members" : null;

  const allOption = { label: `All ${waitlistLabel ?? groupLabel}`, value: "all" };
  const selectOption = {
    label: `Select ${waitlistLabel ?? groupLabel}`,
    value: "select",
  };

  const goBack = () => {
    dispatch(clearNotifications());
    navigate(-1);
  };

  const getNavigation = () => {
    return {
      title: viewOnly ? groupLabel : `Manage ${groupLabel}`,
      leftIcon: faChevronLeft,
      leftButtonClick: goBack,
    };
  };

  const handleModeChange = async (option) => {
    if (!recipientGroup?.id) return;
    const newIncludeAll = option.value === "all";
    await dispatch(
      updateRecipientGroup({
        groupID: recipientGroup.id,
        data: { includeAll: newIncludeAll },
      }),
    );
    setFilter((prev) => ({ ...prev, page: { ...prev.page, number: 1 } }));
  };

  const loadedRecipients = useMemo(
    () => recipientGroup?.recipients ?? [],
    [recipientGroup?.recipients],
  );
  const selectedRecipients = useMemo(
    () =>
      loadedRecipients.filter(
        (r) => r.selectable !== false && r.selected === true,
      ),
    [loadedRecipients],
  );

  const pagination = recipientGroup?.pagination;
  const recipientCount = viewOnly
    ? pagination?.totalCount ?? 0
    : recipientGroup?.selectedCount ?? 0;

  const recipientsLoading = loading?.includes?.("getRecipientGroup") ?? false;
  const groupUpdating = loading?.includes?.("updateRecipientGroup") ?? false;
  const groupLoaded = !!recipientGroup?.id;
  const isInitialLoading =
    !groupLoaded ||
    groupUpdating ||
    (recipientsLoading && filter.page.number === 1 && !silentRefetch.current);
  const hasMore = (pagination?.page ?? 1) < (pagination?.totalPages ?? 1);

  const loadMore = () => {
    if (recipientsLoading || !hasMore) return;
    setFilter((prev) => ({
      ...prev,
      page: { ...prev.page, number: prev.page.number + 1 },
    }));
  };

  const searchEmptyMessage = (
    <GSEmptyList
      title="We couldn't find who you're looking for..."
      detail="Try changing the search term."
      actions={[
        {
          title: "Clear Search",
          onClick: clearSearch,
        },
        ...(recipientGroup?.groupType === "players" && !viewOnly
          ? [
              {
                title: "Add Team",
                buttonIcon: faPlus,
                type: "black",
                onClick: () => navigate(`/${tournamentID}/teams/add`),
              },
            ]
          : []),
      ]}
    />
  );

  const renderRecipient = (recipient) => (
    <RecipientListItem
      recipient={recipient}
      channel={currentMessage?.channel}
      groupType={recipientGroup?.groupType}
      messageStatus={currentMessage?.status}
    />
  );

  const noSentRecipientsMessage = (
    <GSEmptyList
      title="No Message Sent"
      detail="Emails are sent once per email address, even if selected in multiple groups."
      actions={[{ title: "Close", type: "grey", onClick: goBack }]}
    />
  );

  const getEmptyMessage = () => {
    if (filter.q) return searchEmptyMessage;
    if (viewOnly) return noSentRecipientsMessage;
    return (
      <GSEmptyList
        title={`No ${groupLabel} Available`}
        detail={`Once added, ${groupLabel.toLowerCase()} will be displayed here.`}
        actions={
          recipientGroup?.groupType === "players"
            ? [
                {
                  title: "Add Team",
                  buttonIcon: faPlus,
                  type: "black",
                  onClick: () => navigate(`/${tournamentID}/teams/add`),
                },
              ]
            : []
        }
      />
    );
  };

  const setRecipientSelected = (item, selected) => {
    if (item.selectable === false) return;
    dispatch(updateRecipient({ recipientID: item.id, data: { selected } }));
  };

  const getRecipientList = () => {
    const isCheckList = !viewOnly && !isIncludeAll;
    const displayedItems = isInitialLoading ? [] : loadedRecipients;
    const hasItems = displayedItems.length > 0;
    return (
      <GSItemList
        type="vertical recipient-list grey-empty-list rounded-empty-list"
        items={displayedItems}
        listStyle={
          hasItems
            ? undefined
            : {
                ...defaultPadding.xLargePad.apply("horizontal"),
                ...defaultPadding.smallLayoutPad.apply("vertical"),
              }
        }
        loading={isInitialLoading}
        loadingMainText="Loading Recipients..."
        isCheckList={isCheckList}
        selectedItems={isCheckList ? selectedRecipients : undefined}
        itemChecked={
          isCheckList ? (item) => setRecipientSelected(item, true) : undefined
        }
        itemUnchecked={
          isCheckList ? (item) => setRecipientSelected(item, false) : undefined
        }
        listItem={renderRecipient}
        emptyMessage={getEmptyMessage()}
      />
    );
  };

  const getContent = () => {
    const formTitle = viewOnly
      ? `${groupLabel} (${recipientCount})`
      : `Manage ${groupLabel} (${recipientCount})`;

    return (
      <gs-form>
        <GSActionBar type="form-header" header={formTitle} />
        {!viewOnly && (
          <gs-form-section>
            <GSRadioGroup
              isLtr={true}
              options={[allOption, selectOption]}
              selectedOption={isIncludeAll ? allOption : selectOption}
              selectionChanged={handleModeChange}
            />
            <RecipientModeBanner
              groupLabel={groupLabel}
              groupType={recipientGroup?.groupType ?? "recipients"}
              isIncludeAll={isIncludeAll}
            />
          </gs-form-section>
        )}
        <div
          className="recipient-search"
          style={{
            ...defaultPadding.xLargePad.apply("horizontal"),
            ...defaultPadding.largePad.apply("vertical"),
          }}
        >
          <GSInput
            textValue={search}
            onChange={(e) => setSearch(e.target.value)}
            onSubmit={submitSearch}
            placeholder={`Search ${groupLabel.toLowerCase()}...`}
            leftIcon={faSearch}
            rightIcon={faTimesCircle}
            rightIconClick={clearSearch}
          />
        </div>
        {getRecipientList()}
      </gs-form>
    );
  };

  const getDrawer = () => {
    return {
      actions: [
        {
          name: "Done",
          action: goBack,
          type: "light-grey",
          isDisabled: isInitialLoading,
        },
      ],
    };
  };

  const defaultBanner = useMemo(
    () => ({
      title: getNotificationItemInfo(
        "Selections will be automatically saved.",
        faSave,
      ),
      state: "grey",
    }),
    [],
  );

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const [banner] = useNotificationBanner({
    notifications,
    timeoutAction,
    defaultBanner: viewOnly ? undefined : defaultBanner,
  });

  return (
    <tournament-message-recipient-manage>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        content={
          <GSInfiniteList
            loadMore={loadMore}
            filter={{ ...filter, page: { ...filter.page } }}
          >
            {getContent()}
          </GSInfiniteList>
        }
        drawer={viewOnly ? "" : getDrawer()}
      />
    </tournament-message-recipient-manage>
  );
};

export default TournamentMessageRecipientManage;
